import type React from 'react';
import { create } from 'zustand';
import { getState, useState } from '@/framework';
import type { IExportState, StateType } from '@/version-types';
import { getBuiltinShortcutMenuItems } from '../builtin';
import type {
    IShortcutMenuCustomItem,
    IShortcutMenuItem,
    IShortcutMenuLayout,
    ShortcutMenuEmptySelectionRenderer,
} from '../type';

/** 状态 */
interface IState {
    /** 是否显示快捷菜单 */
    visible: boolean;
    /** 是否启用默认工具栏回退 */
    defaultMenuEnabled: boolean;
    /** 是否显示高级 AI 操作 */
    advancedAiEnabled: boolean;
    /** 按图层类型配置的菜单布局 */
    layerMenus: Record<string, IShortcutMenuLayout>;
    /** 默认快捷工具栏项 */
    items: IShortcutMenuItem[];
    /** 业务自定义默认工具栏项 */
    customItems: IShortcutMenuCustomItem[];
    /** 默认工具栏的完整替换内容 */
    replacementMenu: React.ReactNode | null;
    /** 无激活图层时渲染的扩展 */
    emptySelectionRenderers: ShortcutMenuEmptySelectionRenderer[];
}

/** 动作 */
interface IAction {
    setVisible(visible: boolean): void;
    setDefaultMenuEnabled(enabled: boolean): void;
    setAdvancedAiEnabled(enabled: boolean): void;
    setLayerMenu(layerType: string, menu: IShortcutMenuLayout): void;
    setItems(items: IShortcutMenuItem[]): void;
    addItems(items: IShortcutMenuItem[]): void;
    addCustomItem(item: IShortcutMenuCustomItem): void;
    removeCustomItem(layerType: string, key: string): void;
    setReplacementMenu(menu: React.ReactNode | null): void;
    addEmptySelectionRenderer(renderer: ShortcutMenuEmptySelectionRenderer): void;
    removeEmptySelectionRenderer(renderer: ShortcutMenuEmptySelectionRenderer): void;
}

interface IStore extends IState, IAction {}

const createState = (_: string) => {
    return create<IStore>((set, get) => ({
        visible: true,
        defaultMenuEnabled: false,
        advancedAiEnabled: true,
        layerMenus: {},
        items: getBuiltinShortcutMenuItems(),
        customItems: [],
        replacementMenu: null,
        emptySelectionRenderers: [],

        setVisible(visible) {
            set({ visible });
        },
        setDefaultMenuEnabled(defaultMenuEnabled) {
            set({ defaultMenuEnabled });
        },
        setAdvancedAiEnabled(advancedAiEnabled) {
            set({ advancedAiEnabled });
        },
        setLayerMenu(layerType, menu) {
            set((state) => ({
                layerMenus: {
                    ...state.layerMenus,
                    [layerType]: menu,
                },
            }));
        },
        setItems(items) {
            set({ items });
        },
        addItems(items) {
            set({ items: [...get().items, ...items] });
        },
        addCustomItem(item) {
            set((state) => {
                const index = state.customItems.findIndex(
                    (current) => current.layerType === item.layerType && current.key === item.key,
                );
                if (index < 0) {
                    return { customItems: [...state.customItems, item] };
                }

                const customItems = [...state.customItems];
                customItems[index] = item;
                return { customItems };
            });
        },
        removeCustomItem(layerType, key) {
            set((state) => ({
                customItems: state.customItems.filter((item) => item.layerType !== layerType || item.key !== key),
            }));
        },
        setReplacementMenu(replacementMenu) {
            set({ replacementMenu });
        },
        addEmptySelectionRenderer(renderer) {
            if (get().emptySelectionRenderers.includes(renderer)) return;
            set((state) => ({
                emptySelectionRenderers: [...state.emptySelectionRenderers, renderer],
            }));
        },
        removeEmptySelectionRenderer(renderer) {
            set((state) => ({
                emptySelectionRenderers: state.emptySelectionRenderers.filter((item) => item !== renderer),
            }));
        },
    }));
};

/** 状态名称 */
const name = 'shortcut-menu-state';

type ModuleType = StateType<IStore, typeof name>;

export const useShortcutMenuState = () => {
    return useState<ModuleType>()[name];
};

export const getShortcutMenuState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
